/**
 * Armazón del módulo **Autoservicio**.
 *
 * Las cinco pantallas (`/autoservicio`, `/autoservicio/turnos`,
 * `/autoservicio/operaciones`, `/autoservicio/cola` y `/autoservicio/saldos`)
 * comparten el encabezado y la navegación. Los filtros de fechas viven aquí para
 * que cambiar de pantalla no obligue a volver a elegir el rango; la pantalla de
 * saldos trae sus propios filtros (estado, vigencia y paginación).
 *
 * «Actualizar» invalida todas las consultas de `['autoservicio']`: sólo la
 * pantalla visible está habilitada, así que se refresca exactamente lo que se ve.
 */

import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { MonitorSmartphone } from 'lucide-react';
import { useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { selectUser, useAuthStore } from '@/features/auth/auth.store';
import { cn, toIsoDate } from '@/lib/utils';

import type { FiltrosAutoservicio, SeccionAutoservicio } from '../autoservicio.types';
import { FiltrosAutoservicio as BarraFiltros } from '../components/filtros-autoservicio';

interface SeccionConfig {
  clave: SeccionAutoservicio;
  etiqueta: string;
  titulo: string;
  descripcion: string;
  ruta: string;
  end?: boolean;
}

const SECCIONES: SeccionConfig[] = [
  {
    clave: 'resumen',
    etiqueta: 'Dashboard',
    titulo: 'Dashboard de Autoservicio',
    descripcion:
      'KPIs, gráficos y salud del canal: solicitudes, documentación fiscal, comprobantes, uso de códigos de saldo y cola de reintentos.',
    ruta: '/autoservicio',
    end: true,
  },
  {
    clave: 'turnos',
    etiqueta: 'Por turno',
    titulo: 'Operación por turno',
    descripcion:
      'Registros de autoservicio agrupados por turno: documentación, comprobantes, códigos de saldo y montos.',
    ruta: '/autoservicio/turnos',
  },
  {
    clave: 'operaciones',
    etiqueta: 'Operaciones',
    titulo: 'Operaciones de autoservicio',
    descripcion:
      'Detalle por operación: comprobante emitido, montos, código de saldo usado y venta asociada.',
    ruta: '/autoservicio/operaciones',
  },
  {
    clave: 'cola',
    etiqueta: 'Cola de envío',
    titulo: 'Cola de envío',
    descripcion:
      'Salud de la cola de documentación: pendientes por reintentar, fallidos que agotaron los reintentos y reintentos acumulados.',
    ruta: '/autoservicio/cola',
  },
  {
    clave: 'saldos',
    etiqueta: 'Saldos a favor',
    titulo: 'Saldos a favor',
    descripcion:
      'Devolución en efectivo de saldos a favor del autoservicio y ampliación de la vigencia de sus códigos.',
    ruta: '/autoservicio/saldos',
  },
];

/** Filtros con los que abre el módulo: el día de hoy (extremos inclusivos). */
function filtrosIniciales(): FiltrosAutoservicio {
  const hoy = toIsoDate(new Date());

  return { desde: hoy, hasta: hoy, turnoId: '', limite: 100 };
}

/** Sección activa a partir de la ruta (las rutas hijas son fijas). */
function seccionDeRuta(pathname: string): SeccionAutoservicio {
  if (pathname.includes('/saldos')) return 'saldos';
  if (pathname.includes('/turnos')) return 'turnos';
  if (pathname.includes('/operaciones')) return 'operaciones';
  if (pathname.includes('/cola')) return 'cola';

  return 'resumen';
}

/**
 * Encabezado + filtros + pestañas + pantalla de la sección.
 */
export function AutoservicioLayout() {
  const { pathname } = useLocation();
  const seccion = seccionDeRuta(pathname);
  const config = SECCIONES.find((item) => item.clave === seccion) ?? SECCIONES[0]!;

  useDocumentTitle(`Autoservicio · ${config.etiqueta}`);

  const [filtros, setFiltros] = useState<FiltrosAutoservicio>(filtrosIniciales);

  // El servicio responde 400 (M2031) con un rango invertido o un turno que no
  // es entero; se valida antes para no gastar la llamada ni dejar la pantalla
  // con un error que el usuario puede corregir en el propio filtro.
  const rangoInvalido =
    filtros.desde !== '' && filtros.hasta !== '' && filtros.desde > filtros.hasta;
  const turnoInvalido = filtros.turnoId !== '' && !/^\d+$/.test(filtros.turnoId.trim());
  const limiteInvalido = filtros.limite < 1 || filtros.limite > 500;
  // El límite sólo existe en Operaciones: no debe bloquear las demás pantallas.
  const puedeConsultar =
    !rangoInvalido && !turnoInvalido && !(seccion === 'operaciones' && limiteInvalido);

  const cliente = useQueryClient();
  const refrescando = useIsFetching({ queryKey: ['autoservicio'] }) > 0;

  // «Saldos a favor» registra devoluciones en efectivo: la API exige el grupo
  // `logistics`, así que la pantalla y su pestaña sólo se muestran a quien puede
  // usarla (un acceso directo por URL redirige al dashboard del módulo).
  const usuario = useAuthStore(selectUser);
  const puedeVerSaldos =
    Boolean(usuario?.isAdmin) || (usuario?.roles.includes('logistics') ?? false);

  if (seccion === 'saldos' && !puedeVerSaldos) {
    return <Navigate to="/autoservicio" replace />;
  }

  // La pantalla de saldos trae sus propios filtros y su botón de búsqueda.
  const mostrarFiltros = seccion !== 'saldos';

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Autoservicio</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <MonitorSmartphone className="size-6 text-brand-500" aria-hidden="true" />
          {config.titulo}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-ink-500">{config.descripcion}</p>
      </header>

      {mostrarFiltros ? (
        <BarraFiltros
          filtros={filtros}
          onChange={setFiltros}
          onLimpiar={() => setFiltros(filtrosIniciales())}
          onRefrescar={() => void cliente.invalidateQueries({ queryKey: ['autoservicio'] })}
          refrescando={refrescando}
          seccion={seccion}
        />
      ) : null}

      {mostrarFiltros && rangoInvalido ? (
        <Alert variant="warning" title="Rango de fechas inválido">
          «Desde» ({filtros.desde}) no puede ser posterior a «Hasta» ({filtros.hasta}).
        </Alert>
      ) : null}

      {mostrarFiltros && turnoInvalido ? (
        <Alert variant="warning" title="Turno inválido">
          El turno debe ser un número entero positivo (por ejemplo, 232).
        </Alert>
      ) : null}

      {mostrarFiltros && seccion === 'operaciones' && limiteInvalido ? (
        <Alert variant="warning" title="Límite inválido">
          El límite debe estar entre 1 y 500 filas.
        </Alert>
      ) : null}

      <nav
        aria-label="Pantallas de autoservicio"
        className="inline-flex w-fit max-w-full flex-wrap gap-1 rounded-xl border border-ink-100 bg-white p-1 shadow-soft"
      >
        {SECCIONES.filter((opcion) => opcion.clave !== 'saldos' || puedeVerSaldos).map((opcion) => (
          <NavLink
            key={opcion.clave}
            to={opcion.ruta}
            end={opcion.end}
            className={({ isActive }) =>
              cn(
                'rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-brand-500 text-white shadow-sm'
                  : 'text-ink-500 hover:bg-ink-100 hover:text-ink-800',
              )
            }
          >
            {opcion.etiqueta}
          </NavLink>
        ))}
      </nav>

      <Outlet context={{ filtros, puedeConsultar, seccion }} />
    </div>
  );
}
